import { LayoutType, PedagoPlayer, PlayerSelectionContainer } from "@kalyzee/escaet-ui";
import { isMeetingSessionRoleLessThan, MeetingSessionOvenMediaReceiver, MeetingSessionOvenMediaReceiverManagerBaseData } from "@kalyzee/kast-web-sdk";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { Meeting, MeetingSessionRole } from "@kalyzee/kast-web-sdk/websocket";
import React, { ForwardedRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { MeetingPlayerTriLayoutContainer } from "../../../components/player/container/MeetingPlayerTriLayoutContainer";
import { PlayerItem } from "../../../components/player/interfaces";
import MeetingSessionMediaPlayer, { MeetingSessionMediaPlayerProps } from "../../../components/player/MeetingSessionMediaPlayer";
import { pushDeviceControlsCmd } from "../../../hooks/deviceControls";
import { useMeetingSelector } from "../../../hooks/meeting";
import { PopulatedMeetingData } from "../../../interfaces/populatedData";
import { PedagogyContextState } from "../../../pages/master/pedagogy/context";
import styles from "./studentDashboardContainer.module.css";
import { expertWebcamName } from "../../../helpers/webcam";

export interface PedagogyStudentDashboardContainerProps {
  meetingContainerAPI?: MeetingContainerAPI;
  meeting?: Meeting;
  populatedMeetingData?: PopulatedMeetingData;
  context?: PedagogyContextState;
  layoutType: LayoutType;
  audioValidator?: (session: MeetingSessionOvenMediaReceiver) => boolean;
  style?: React.CSSProperties;
  className?: string;
}

export interface PedagogyStudentDashboardContainerRef {
  switchPlayerSelector: () => void;
}

export const PedagogyStudentDashboardContainer = React.forwardRef(
  (
    { meetingContainerAPI, meeting, populatedMeetingData, context, layoutType, audioValidator, style, className }: PedagogyStudentDashboardContainerProps,
    forwardRef: ForwardedRef<PedagogyStudentDashboardContainerRef>
  ) => {
    const [receptionSessions] = useMeetingSelector(meetingContainerAPI, (state) => state.receptionSessions);
    const [sessionIdFocused, setSessionIdFocused] = useState<{ id: string; mode: "overlay" | "fullscreen" }>();
    const contextRef = useRefAutoUpdate(context);
    const sessions = useMemo(() => {
      const expertSessions =
        receptionSessions?.filter((s) => {
          if (isMeetingSessionRoleLessThan(s.data.session.role, MeetingSessionRole.MODERATOR)) return false;
          return s.data.mediaSession.name !== expertWebcamName;
        }) ?? [];
      return expertSessions;
    }, [receptionSessions, meeting]); // add "meeting" in dependencies to force refresh if meeting updated
    const { enabledVideoSessions } = useMemo(() => {
      const disabledSessions = context?.disabledExpertMediaSessionIds ?? [];
      const enabledVideoSessions = sessions.filter((s) => !disabledSessions.find((id) => id === s.data.mediaSession?.id));
      return { enabledVideoSessions };
    }, [sessions, context?.disabledExpertMediaSessionIds]);

    const playerItems = useMemo(
      () =>
        enabledVideoSessions.map(
          (session) =>
            new PlayerItem({
              type: "video_session",
              videoSession: session,
            })
        ),
      [enabledVideoSessions]
    );
    const [displayPlayerSelector, setDisplayPlayerSelector] = useState(false);

    useImperativeHandle(
      forwardRef,
      () => ({
        switchPlayerSelector: () => {
          setDisplayPlayerSelector((v) => !v);
        },
      }),
      []
    );

    // ---------------------------------------------------------------------- //
    // ---------------------------   UTILS ----------------------------- //
    // ---------------------------------------------------------------------- //

    const getLabel = useCallback((session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>) => {
      return session.data.mediaSession.extra?.label ?? "Caméra";
    }, []);

    // ---------------------------------------------------------------------- //
    // ---------------------------   EFFECT ----------------------------- //
    // ---------------------------------------------------------------------- //

    useEffect(() => {
      if (displayPlayerSelector) setSessionIdFocused(undefined);
    }, [displayPlayerSelector]);

    useEffect(() => {
      if (!audioValidator) return;
      const validator = contextRef.current?.addAudioValidator((session) => {
        return audioValidator(session);
      });
      return () => {
        validator?.flush();
      };
    }, [audioValidator]);

    // ---------------------------------------------------------------------- //
    // ---------------------------   RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    if (!meeting) return null;

    const renderPlayer = (
      session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>,
      mode: "classic" | "overlay" | "fullscreen",
      style?: React.CSSProperties,
      playerProps?: MeetingSessionMediaPlayerProps
    ) => {
      const deviceId = session.data.mediaSession.deviceId;
      const controls = session.data.mediaSession.extra?.controls;
      const room = meeting.room?.id;
      return (
        <PedagoPlayer
          style={{ width: "100%", height: "100%", borderRadius: "8px", overflow: "hidden", ...(style ?? {}) }}
          label={getLabel(session)}
          extendButton={mode === "overlay"}
          reduceButton={mode === "fullscreen"}
          closeButton={mode === "overlay" || mode === "fullscreen"}
          cameraControls={room && controls && deviceId && (mode === "fullscreen" || mode === "overlay")}
          onClick={
            mode === "classic"
              ? () => {
                  setDisplayPlayerSelector(false);
                  setSessionIdFocused({ id: session.getId(), mode: "overlay" });
                }
              : undefined
          }
          onReduce={() => {
            if (mode !== "fullscreen" || !sessionIdFocused) return;
            setSessionIdFocused({ id: sessionIdFocused.id, mode: "overlay" });
          }}
          onExpend={() => {
            if (mode !== "overlay" || !sessionIdFocused) return;
            setSessionIdFocused({ id: sessionIdFocused.id, mode: "fullscreen" });
          }}
          onClose={() => {
            setSessionIdFocused(undefined);
          }}
          onMoveCameraControls={(on: boolean, direction: "up" | "down" | "left" | "right" | "up-left" | "up-right" | "down-left" | "down-right") => {
            if (!room || !deviceId) return;
            if (direction.startsWith("up")) pushDeviceControlsCmd(room, deviceId, on ? "move" : "stopMove", { direction: "up" });
            else if (direction.startsWith("down")) pushDeviceControlsCmd(room, deviceId, on ? "move" : "stopMove", { direction: "down" });
            if (direction.endsWith("left")) pushDeviceControlsCmd(room, deviceId, on ? "move" : "stopMove", { direction: "left" });
            else if (direction.endsWith("right")) pushDeviceControlsCmd(room, deviceId, on ? "move" : "stopMove", { direction: "right" });
          }}
          onZoomCameraControls={(on: boolean, direction: "in" | "out") => {
            if (!room || !deviceId) return;
            pushDeviceControlsCmd(room, deviceId, on ? "zoom" : "stopZoom", { direction });
          }}
          onDefaultPresetCameraControls={() => {
            if (!room || !deviceId) return;
            pushDeviceControlsCmd(room, deviceId, "loadPreset", { index: 1 });
          }}
        >
          <MeetingSessionMediaPlayer
            style={{ width: "100%", height: "100%", maxHeight: "100%" }}
            {...(playerProps ?? {})}
            meeting={meeting}
            sessionId={session.data.session.id}
            mediaSessionId={session.data.mediaSession?.id!}
            ovenMediaSession={session}
            muted={true}
            hideSoundEffect
            callbacks={{
              playingSound: (v) => {
                playerProps?.callbacks?.playingSound?.(v);
              },
            }}
          />
        </PedagoPlayer>
      );
    };

    const renderOverlayPlayer = () => {
      if (!sessionIdFocused) return null;
      if (sessionIdFocused.mode !== "overlay") return null;
      const session = enabledVideoSessions.find((s) => s.getId() === sessionIdFocused.id);
      if (!session) return null;
      return <div className={styles.overlayPlayer}>{renderPlayer(session, "overlay")}</div>;
    };

    const renderFullscreenPlayer = () => {
      if (!sessionIdFocused) return null;
      if (sessionIdFocused.mode !== "fullscreen") return null;
      const session = enabledVideoSessions.find((s) => s.getId() === sessionIdFocused.id);
      if (!session) return null;
      return <div className={styles.fullscreenPlayer}>{renderPlayer(session, "fullscreen")}</div>;
    };

    const renderPlayerSelector = () => {
      if (!displayPlayerSelector) return null;
      return (
        <div className={styles.playerSelectorContainer}>
          {sessions.map((s) => {
            const enabled = enabledVideoSessions.find((curr) => curr.getId() === s.getId()) ? true : false;
            return (
              <PlayerSelectionContainer
                enabled={enabled}
                onEnable={(v: boolean) => {
                  const mediaSessionId = s.data.mediaSession?.id;
                  if (!mediaSessionId) return;
                  if (v) contextRef.current?.enableExpertMediaSessionId(mediaSessionId);
                  else contextRef.current?.disableExpertMediaSessionId(mediaSessionId);
                }}
                label={getLabel(s)}
                style={{ width: "100%" }}
                key={s.getId()}
              >
                <MeetingSessionMediaPlayer
                  style={{ width: "100%", height: "150px" }}
                  meeting={meeting}
                  sessionId={s.data.session.id}
                  mediaSessionId={s.data.mediaSession?.id!}
                  ovenMediaSession={s}
                  muted={true}
                />
              </PlayerSelectionContainer>
            );
          })}
        </div>
      );
    };

    return (
      <div className={`${styles.container}${className ? ` ${className}` : ""}`} style={style}>
        <MeetingPlayerTriLayoutContainer
          meeting={meeting}
          items={playerItems}
          layoutProps={{ square: { noPages: true } }}
          renderItem={(rendererProvider, item) => {
            const itemData = item.getData();
            if (itemData.type !== "video_session") return rendererProvider(); // should not happen
            return renderPlayer(itemData.videoSession, "classic");
          }}
          layout={layoutType}
        />
        {renderOverlayPlayer()}
        {renderFullscreenPlayer()}
        {renderPlayerSelector()}
      </div>
    );
  }
);
